<template>
  <div class="card">
    <div class="card-header cursor-pointer">
      <div
        class="card-title m-0 d-flex justify-content-between align-items-center"
      >
        <h3 class="fw-bold m-0">{{ $t('global.basic') }}</h3>

        <b-dropdown
          class="btn-sm rounded-sm m-0 p-0"
          variant="light"
          text="..."
          no-caret
          right
        >
          <b-dropdown-item v-b-modal.update-password-modal>{{
            $t('account.updatePassword')
          }}</b-dropdown-item>
          <b-dropdown-item v-b-modal.update-email-modal>{{
            $t('account.updateEmail')
          }}</b-dropdown-item>
          <b-dropdown-item v-b-modal.update-phone-modal>{{
            $t('account.updatePhone')
          }}</b-dropdown-item>
          <b-dropdown-item v-b-modal.customer-agent-modal>{{
            $t('account.updateCurrency')
          }}</b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-card>
          <b-skeleton width="85%"></b-skeleton>
          <b-skeleton width="55%"></b-skeleton>
          <b-skeleton width="70%"></b-skeleton>
        </b-card>
      </template>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <Input
              label="global.name"
              id="name"
              v-model="form.name"
              :state="validateState('name')"
              :defaultValue="user.name"
              labelStyle="2"
            />

            <Input
              label="global.surname"
              id="surname"
              v-model="form.surname"
              :state="validateState('surname')"
              :defaultValue="user.surname"
              labelStyle="2"
            />

            <div class="row mb-3 align-items-center">
              <label class="col-md-4 mt-n4 text-muted">{{
                $t('account.gender')
              }}</label>
              <div class="col-lg-8">
                <b-form-group :state="validateState('gender')">
                  <multiselect
                    id="multiselectGender"
                    v-model="form.gender"
                    :options="genders"
                    :allowEmpty="false"
                    :show-labels="false"
                    :searchable="false"
                    :placeholder="$t('account.gender')"
                    :limit="3"
                    :show-no-results="false"
                    :custom-label="customGenderLabel"
                  />
                </b-form-group>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <Input
              label="global.email"
              id="mail"
              v-model="form.email"
              :defaultValue="user.email"
              readonly="readonly"
              labelStyle="2"
            />

            <Input
              label="account.phone"
              id="gsm"
              v-model="form.gsm"
              :defaultValue="userCellPhone"
              readonly="readonly"
              labelStyle="2"
            />

            <!-- <Input label="verification.birthDateWithFormat"
                   id="birthDateWithFormat"
                   v-model="form.birthday"
                   :defaultValue="userBirthdate"
                   v-mask="['##/##/####']"
                   :state="validateState('birthday')"
                   labelStyle="2" /> -->

            <DateTime
              label="verification.birthDateWithFormat"
              placeholder="verification.birthDateWithFormat"
              v-model="form.birthday"
              valueFormat="YYYY-MM-DD"
              :defaultValue="userBirthdate"
              :langVal="$i18n.locale"
              labelStyle="2"
            ></DateTime>
          </div>
        </div>
      </div>

      <div class="card-header cursor-pointer">
        <div
          class="card-title m-0 d-flex justify-content-between align-items-center"
        >
          <h3 class="fw-bold m-0">{{ $t('global.preferences') }}</h3>
        </div>
      </div>

      <div class="card-body">
        <div class="row">
          <div class="col-6">
            <div class="row mb-3 align-items-center">
              <label class="col-md-4 mt-n4 text-muted">{{
                $t('account.country')
              }}</label>
              <div class="col-lg-8">
                <b-form-group :state="validateState('country')">
                  <multiselect
                    id="multiselectCountry"
                    v-model="form.country"
                    :options="countries"
                    :allowEmpty="false"
                    :show-labels="false"
                    :searchable="true"
                    :placeholder="$t('account.country')"
                    :limit="3"
                    :show-no-results="false"
                    :custom-label="customCountryLabel"
                  />
                </b-form-group>
              </div>
            </div>

            <Input
              label="global.currency"
              id="currency"
              v-model="form.currency"
              :defaultValue="userCurrency"
              readonly="readonly"
              labelStyle="2"
            />
          </div>
          <div class="col-6">
            <div class="row mb-3 align-items-center">
              <label class="col-md-4 mt-n4 text-muted">{{
                $t('global.language')
              }}</label>
              <div class="col-lg-8">
                <b-form-group :state="validateState('language')">
                  <multiselect
                    id="multiselectLanguage"
                    v-model="form.language"
                    :options="languages"
                    :allowEmpty="false"
                    :show-labels="false"
                    :searchable="true"
                    :placeholder="$t('global.language')"
                    :limit="3"
                    :show-no-results="false"
                    :custom-label="customLangLabel"
                  />
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer d-flex justify-content-end py-6 px-9">
        <b-button
          type="reset"
          class="rounded-sm mr-3"
          variant="light"
          @click="cancel()"
          >{{ $t('global.cancel') }}</b-button
        >

        <b-button
          type="submit"
          class="rounded-sm"
          variant="primary"
          @click="confirm()"
          >{{ $t('global.save') }}</b-button
        >
      </div>
    </b-skeleton-wrapper>

    <CustomerAgentModal
      :user="user"
      :title="$t('account.updateCurrencyTitle')"
      :description="
        $t('account.updateCurrencyDescription') + ' : ' + form.currency
      "
    />

    <UpdatePasswordModal :user="user" />
    <UpdateEmailModal :user="user" />
    <UpdatePhoneModal :user="user" />
  </div>
</template>

<script>
// Utils
import ApiService from '@/core/services/api.service'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import moment from 'moment'

import i18nService from '@/core/services/i18n.service.js'

export default {
  name: 'Settings',
  mixins: [validationMixin],
  props: {
    user: Array | String,
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        surname: '',
        email: '',
        gender: '',
        birthday: '',
        country: '',
        language: '',
        currency: '',
      },
      genders: ['male', 'female'],
      countries: [],
      languages: [
        { text: 'Turkish', value: 'tr' },
        { text: 'Dutch', value: 'nl' },
        { text: 'English (Global)', value: 'us' },
        { text: 'English (Australia)', value: 'au' },
        { text: 'English (New Zealand)', value: 'nz' },
      ],
    }
  },
  validations: {
    form: {
      name: {
        required,
      },
      surname: {
        required,
      },
      gender: {
        required,
      },
      birthday: {
        required,
      },
      country: {
        required,
      },
      language: {
        required,
      },
    },
  },
  computed: {
    userCellPhone() {
      return (
        (this.user.cell_phone_code ? this.user.cell_phone_code : '') +
        ' ' +
        (this.user.cell_phone ? this.user.cell_phone : '')
      )
    },
    userBirthdate() {
      return moment(this.user.birthday).format('YYYY-MM-DD')
    },
    userCurrency() {
      return this.user.currentAccount && this.user.currentAccount.data
        ? this.user.currentAccount.data.currency_code
        : '-'
    },
  },
  mounted() {
    this.setFormData()
  },
  created() {
    this.getAndSetUserCountries()
  },
  methods: {
    setFormData() {
      this.form.gender = this.user.gender

      let selectedDefault_locale = this.user.default_locale.toLowerCase()
      if (selectedDefault_locale === 'en') {
        selectedDefault_locale = 'us'
      }
      this.form.language = this.languages.filter(
        (f) => f.value.toLowerCase() == selectedDefault_locale
      )
    },
    customGenderLabel(item) {
      return this.$t('gender.' + item)
    },
    customLangLabel({ text, value }) {
      return `${value.toUpperCase()} - ${text}`
    },
    customCountryLabel({ name }) {
      return name
    },
    getAndSetUserCountries() {
      this.getCountries().then((res) => {
        this.setCountries(res)

        if (this.user.country_code) {
          let findCountry = this.countries.filter(
            (f) => f.code.toLowerCase() == this.user.country_code.toLowerCase()
          )
          this.form.country = findCountry
        }
      })
    },
    getCountries() {
      return new Promise((resolve, reject) => {
        ApiService.get('countries')
          .then(({ data }) => {
            resolve(data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    setCountries(res) {
      this.countries = res.data
    },
    changePassword() {
      // password control regex
      let isContainNumReg = /[0-9]/g
      let isContainSpecialReg = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/g
      let isContainUpper = /[A-Z]/g
      let isContainLower = /[a-z]/g

      // set valid
      if (this.form.password.length >= 8) {
        this.validPassword.isMinEightChar || this.validPassword.strongNumber++
        this.validPassword.isMinEightChar = true
      } else {
        !this.validPassword.isMinEightChar || this.validPassword.strongNumber--
        this.validPassword.isMinEightChar = false
      }

      if (basicValidate.patternControl(this.form.password, isContainNumReg)) {
        this.validPassword.isMinOneNumber || this.validPassword.strongNumber++
        this.validPassword.isMinOneNumber = true
      } else {
        !this.validPassword.isMinOneNumber || this.validPassword.strongNumber--
        this.validPassword.isMinOneNumber = false
      }

      if (
        basicValidate.patternControl(this.form.password, isContainSpecialReg)
      ) {
        this.validPassword.isMinOneSpecial || this.validPassword.strongNumber++
        this.validPassword.isMinOneSpecial = true
      } else {
        !this.validPassword.isMinOneSpecial || this.validPassword.strongNumber--
        this.validPassword.isMinOneSpecial = false
      }

      if (basicValidate.patternControl(this.form.password, isContainUpper)) {
        this.validPassword.isMinOneUpper || this.validPassword.strongNumber++
        this.validPassword.isMinOneUpper = true
      } else {
        !this.validPassword.isMinOneUpper || this.validPassword.strongNumber--
        this.validPassword.isMinOneUpper = false
      }

      if (basicValidate.patternControl(this.form.password, isContainLower)) {
        this.validPassword.isMinOneLower || this.validPassword.strongNumber++
        this.validPassword.isMinOneLower = true
      } else {
        !this.validPassword.isMinOneLower || this.validPassword.strongNumber--
        this.validPassword.isMinOneLower = false
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    confirm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        return
      }

      this.loading = true
      Promise.all([this.userUpdate()])
        .then((result) => {
          this.$generateToast(this, 'success', this.$t('global.successful'))
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
        })
    },
    userUpdate() {
      return new Promise((resolve, reject) => {
        let countryCode = this.form.country.code
        if (!countryCode) countryCode = this.form.country[0].code

        let selectedLang = this.form.language.value
        if (!selectedLang) selectedLang = this.form.language[0].value
        if (
          selectedLang !== 'tr' &&
          selectedLang !== 'en' &&
          selectedLang !== 'nl'
        ) {
          selectedLang = 'en'
        }

        let userParams = {
          name: this.form.name,
          surname: this.form.surname,
          gender: this.form.gender.toUpperCase(),
          // birthday: moment(this.form.birthday, 'MM-DD-YYYY').format(
          //   'YYYY-MM-DD'
          // ),
          birthday: this.form.birthday,
          country_code: countryCode,
          default_locale: selectedLang.toUpperCase(),
        }

        // ApiService.put(`users/${this.user.id}`, userParams)
        ApiService.put(`users/me`, userParams)
          .then((res) => {
            this.$i18n.locale = selectedLang
            i18nService.setActiveLanguage(this.$i18n.locale)
            this.$store.dispatch('setCurrentLang', this.$i18n.locale)
            resolve(res.data.data)
          })
          .catch((err) => {
            this.$generateToast(this, 'danger', err.message, true)
            reject(err)
          })
      })
    },
    cancel() {
      this.$emit('changeComponent', 'Overview')
    },
  },
  watch: {
    user(newVal) {
      this.setFormData()
    },
  },
  components: {
    CustomerAgentModal: () =>
      import('@/components/account/modals/CustomerAgentModal'),
    UpdatePasswordModal: () =>
      import('@/components/account/modals/UpdatePasswordModal'),
    UpdateEmailModal: () =>
      import('@/components/account/modals/UpdateEmailModal'),
    UpdatePhoneModal: () =>
      import('@/components/account/modals/UpdatePhoneModal'),
  },
}
</script>
